import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * InteractiveTrajectory - Shows current vs simulated trajectory side-by-side
 * Used in coaching modal to visualize impact of metric adjustments
 */
export const InteractiveTrajectory = ({ currentShots, simulatedParams }) => {
  const trajectoryData = useMemo(() => {
    if (!currentShots || currentShots.length === 0) return [];

    // Calculate average trajectory from current shots
    const avgCarry = currentShots.reduce((sum, s) => sum + (s.carry || 0), 0) / currentShots.length;
    const avgHeight = currentShots.reduce((sum, s) => sum + (s.height || 0), 0) / currentShots.length;

    // Generate arc for current trajectory (simplified parabola)
    const currentArc = [];
    for (let x = 0; x <= avgCarry; x += avgCarry / 50) {
      const t = x / avgCarry;
      const y = 4 * avgHeight * t * (1 - t);  // Parabolic approximation
      currentArc.push({ distance: x, current: y });
    }

    // If we have simulated params, calculate new trajectory
    if (simulatedParams) {
      // TODO: Call /api/analyze/trajectory with simulatedParams to get real trajectory
      // For now, approximate based on parameter changes
      const launchAngleDelta = (simulatedParams.launch_angle || 12) - 12;
      const heightMultiplier = 1 + (launchAngleDelta * 0.1);
      const carryMultiplier = 1 + (launchAngleDelta * 0.02);

      const simCarry = avgCarry * carryMultiplier;
      const simHeight = avgHeight * heightMultiplier;

      for (let i = 0; i < currentArc.length; i++) {
        const t = i / currentArc.length;
        const x = simCarry * t;
        const y = 4 * simHeight * t * (1 - t);
        currentArc[i].simulated = y;
      }
    }

    return currentArc;
  }, [currentShots, simulatedParams]);

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trajectoryData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />

          <XAxis
            dataKey="distance"
            label={{ value: 'Distance (yards)', position: 'insideBottom', offset: -10, fill: '#9ca3af' }}
            tick={{ fill: '#9ca3af' }}
          />

          <YAxis
            label={{ value: 'Height (feet)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
            tick={{ fill: '#9ca3af' }}
          />

          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
            labelStyle={{ color: '#f3f4f6' }}
          />

          <Legend />

          <Line
            type="monotone"
            dataKey="current"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={false}
            name="Your Current Average"
          />

          {simulatedParams && (
            <Line
              type="monotone"
              dataKey="simulated"
              stroke="#10b981"
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              name="Simulated Adjustment"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
